import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../../../services/auth';
import './ResetPassword.css';

const ResetPassword = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [username, setUsername] = useState('');
  const [totpWasEnabled, setTotpWasEnabled] = useState(false);

  const verifyToken = useCallback(async () => {
    try {
      const response = await authService.verifyResetToken(token);
      if (response.valid) {
        setTokenValid(true);
        setUsername(response.username);
        setTotpWasEnabled(response.totpEnabled);
      } else {
        setTokenError(response.message || t('auth:resetPassword.invalid_token'));
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setTokenError(t('auth:resetPassword.invalid_token'));
    } finally {
      setVerifying(false);
    }
  }, [token, t]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = () => {
    if (formData.password.length < 6) {
      setError(t('auth:resetPassword.password_too_short'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth:resetPassword.passwords_dont_match'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);

    try {
      await authService.resetPassword(token, formData.password);
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.message || t('auth:resetPassword.error'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-form">
          <div className="loading-spinner"></div>
          <p className="verifying-text">{t('auth:resetPassword.verifying_token')}</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-form">
          <div className="error-icon">✕</div>
          <h2 className="reset-password-title">
            {t('auth:resetPassword.invalid_token_title')}
          </h2>
          <p className="error-description">
            {tokenError}
          </p>
          <div className="form-actions">
            <Link to="/forgot-password" className="action-link">
              {t('auth:resetPassword.request_new_link')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-form">
          <div className="success-icon">✓</div>
          <h2 className="reset-password-title">
            {t('auth:resetPassword.success_title')}
          </h2>
          <p className="success-message">
            {t('auth:resetPassword.success_message')}
          </p>
          {totpWasEnabled && (
            <div className="totp-notice">
              <strong>{t('auth:resetPassword.totp_disabled_title')}</strong>
              <p>{t('auth:resetPassword.totp_disabled_message')}</p>
            </div>
          )}
          <p className="redirect-message">
            {t('auth:resetPassword.redirecting')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-form">
        <h2 className="reset-password-title">
          {t('auth:resetPassword.title')}
        </h2>
        <p className="reset-password-subtitle">
          {t('auth:resetPassword.subtitle', { username })}
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">
              {t('auth:resetPassword.new_password')}:
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="form-input"
              placeholder={t('auth:resetPassword.password_placeholder')}
              disabled={loading}
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('auth:resetPassword.confirm_password')}:
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="form-input"
              placeholder={t('auth:resetPassword.confirm_placeholder')}
              disabled={loading}
            />
          </div>

          <div className="password-requirements">
            <p className="requirements-title">{t('auth:resetPassword.requirements_title')}:</p>
            <ul>
              <li className={formData.password.length >= 6 ? 'valid' : ''}>
                {t('auth:resetPassword.requirement_length')}
              </li>
              <li className={formData.password && formData.password === formData.confirmPassword ? 'valid' : ''}>
                {t('auth:resetPassword.requirement_match')}
              </li>
            </ul>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.password || !formData.confirmPassword}
            className="submit-button"
          >
            {loading ? t('common:actionStates.saving') : t('auth:resetPassword.submit_button')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;